import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';
import { useDashboardData } from '../hooks/useDashboard/useDashboardData';
import { useLiveSession } from '../hooks/useDashboard/useLiveSession';
import { useConsultantSessionRequests } from '../hooks/useDashboard/useConsultantSessionRequests';
import DashboardHeader from './Dashboard/DashboardHeader';
import DashboardLoadingBanner from './Dashboard/DashboardLoadingBanner';
import DashboardStatsOverview from './Dashboard/DashboardStatsOverview';
import DashboardLiveSessionStatus from './Dashboard/DashboardLiveSessionStatus';
import DashboardConsultantSessionRequests from './Dashboard/DashboardConsultantSessionRequests';
import DashboardAssessmentsSection from './Dashboard/DashboardAssessmentsSection';
import DashboardRecommendationsSection from './Dashboard/DashboardRecommendationsSection';

// Dashboard Component with Dark Mode
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const {
    assessmentsLoading,
    recommendationsLoading,
    recentAssessments,
    topRecommendations,
    dashboardData,
    assessmentsError,
    recommendationsError,
    refreshDashboard
  } = useDashboardData();

  // Refresh dashboard data when navigating back to it from assessment pages
  // This must be after the hook call to access refreshDashboard
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;
    
    // Check if we're on dashboard and came from assessment pages
    if (currentPath === '/dashboard' && prevPath !== '/dashboard') {
      const assessmentPaths = ['/emha', '/assessment/self-guided', '/live-session'];
      if (assessmentPaths.some(path => prevPath.startsWith(path))) {
        logger.log('🔄 [Dashboard] Detected navigation back from assessment page, refreshing data');
        // Small delay to ensure component is fully mounted and stats are preserved
        // Stats cache will prevent showing 0 during refresh
        setTimeout(() => {
          if (refreshDashboard) {
            refreshDashboard();
          }
        }, 100);
      }
    }
    
    prevPathRef.current = currentPath;
  }, [location.pathname, refreshDashboard]);

  const {
    liveSession,
    sessionLoading,
    completingSession,
    handleCancelSessionRequest,
    handleCompleteSession
  } = useLiveSession();

  const {
    requestingSession,
    hasPendingAssignmentRequest,
    handleRequestConsultantSession,
    hasConsultant
  } = useConsultantSessionRequests();

  const handleAddToProject = (recommendation) => {
    // Store recommendation in sessionStorage for the modal
    sessionStorage.setItem('pendingProject', JSON.stringify(recommendation));
    // Navigate to projects page which will show the modal
    navigate('/projects?action=create');
  };

  const { stats } = dashboardData || {};

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <DashboardHeader />

          {/* Loading Banner - Only show for assessments, not recommendations */}
          <DashboardLoadingBanner
            isLoading={assessmentsLoading}
          />

          {/* Overview Stats */}
          <DashboardStatsOverview
            assessmentsLoading={assessmentsLoading}
            stats={stats}
          />

          {/* Live Session Status */}
          <DashboardLiveSessionStatus
            liveSession={liveSession}
            sessionLoading={sessionLoading}
            completingSession={completingSession}
            handleCancelSessionRequest={handleCancelSessionRequest}
            handleCompleteSession={handleCompleteSession}
          />

          {/* Available Assessments - Request Consultant Session */}
          <DashboardConsultantSessionRequests
            sessionLoading={sessionLoading}
            liveSession={liveSession}
            requestingSession={requestingSession}
            hasPendingAssignmentRequest={hasPendingAssignmentRequest}
            hasConsultant={hasConsultant}
            handleRequestConsultantSession={handleRequestConsultantSession}
          />

          {/* Recent Assessments Section */}
          <DashboardAssessmentsSection
            assessmentsLoading={assessmentsLoading}
            assessmentsError={assessmentsError}
            recentAssessments={recentAssessments}
            userEmail={user?.email}
          />

          {/* Top Recommendations Section */}
          <DashboardRecommendationsSection
            recommendationsLoading={recommendationsLoading}
            recommendationsError={recommendationsError}
            topRecommendations={topRecommendations}
            handleAddToProject={handleAddToProject}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


